import useTrackEvent from '@/composables/mixpanel/useTrackEvent';
import { eventNames } from '@/constants/eventNames';
import echoInit from '@/echo.js';
import api from '@/services/axios';
import {
  clearStoredAuth,
  getAuth,
  getConfig,
  getStoredAuth,
  isStoredAuthValid,
  knoxLogin,
  logout,
  redirectToProvider,
} from '@disguise/aries-auth-js';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
export const useUserStore = defineStore('user', () => {
  const user = ref(null);
  const knoxToken = ref(null);
  const knoxExpiry = ref(null);
  const followingUser = ref(null);
  const authStatus = ref(null);

  // A stored Knox token is enough to treat the client as authenticated.
  // Any stale token is handled by the API interceptor and callback flow.
  const isAuthenticated = computed(() => Boolean(knoxToken.value));

  const isLoading = ref(false);

  const clearAuthState = () => {
    clearStoredAuth();
    user.value = null;
    knoxToken.value = null;
    knoxExpiry.value = null;
    followingUser.value = null;
    authStatus.value = null;
    sessionStorage.removeItem('auth_attempts');
  };

  const checkAuthStatus = async ({ forceFreshToken = false } = {}) => {
    isLoading.value = true;
    try {
      // Reuse stored token if it's still valid — no network calls needed
      if (!forceFreshToken && isStoredAuthValid()) {
        const stored = getStoredAuth();
        knoxToken.value = stored.token;
        knoxExpiry.value = stored.expiry;

        if (knoxToken.value) {
          echoInit(knoxToken.value);
        }
        return;
      }

      // No stored token or expired — need session to mint a new one
      const response = await getAuth();
      authStatus.value = response;

      if (response?.status === 200 && response?.data?.user) {
        user.value = response.data.user;

        const knoxAuth = await knoxLogin();
        knoxToken.value = knoxAuth.token;
        knoxExpiry.value = knoxAuth.expiry;

        if (knoxToken.value) {
          echoInit(knoxToken.value);
        }
      }
    } catch (error) {
      console.error('Auth status check failed:', error);
      if (error.message.includes('Failed to fetch') || error.message.includes('Connection refused')) {
        console.warn('Backend not available, user will need to authenticate when backend is ready');
      }
      clearAuthState();
    } finally {
      isLoading.value = false;
    }
  };

  const loginWithProvider = async (provider = 'oidc_auth0', callbackPath = '/') => {
    try {
      // Fetch CSRF token first
      await getConfig();

      // Redirect to provider
      redirectToProvider(provider, callbackPath);
    } catch (error) {
      console.error('Login failed:', error);
      if (error.message.includes('Failed to fetch') || error.message.includes('Connection refused')) {
        // If backend is not available, show a user-friendly message
        alert('Authentication service is not available. Please try again later or contact support.');
      }
      throw error;
    }
  };

  const logoutUser = async () => {
    try {
      await logout();
      clearAuthState();

      // Redirect to Auth0 logout if needed
      const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN;
      const clientId = import.meta.env.VITE_AUTH0_CLIENTID;

      if (auth0Domain && clientId) {
        const returnTo = encodeURIComponent(window.location.origin);
        const logoutUrl = `https://${auth0Domain}/v2/logout?client_id=${clientId}&returnTo=${returnTo}`;
        window.location.href = logoutUrl;
      } else {
        // If Auth0 is not configured, just redirect to home page
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Logout failed:', error);
      // On error, redirect to home page
      window.location.href = '/';
    }
  };

  const getHeaders = () => {
    return {
      Authorization: `Bearer ${knoxToken.value}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
  };

  const hasAccessToken = () => {
    return knoxToken.value !== null;
  };

  const getAccessToken = () => {
    return knoxToken.value;
  };

  const refreshToken = async () => {
    // Try to get fresh Knox token
    try {
      if (hasAccessToken()) {
        const knoxAuth = await knoxLogin();
        knoxToken.value = knoxAuth.token;
        knoxExpiry.value = knoxAuth.expiry;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Force re-authentication
      await checkAuthStatus({ forceFreshToken: true });
    }
  };

  const followUser = (userToFollow) => {
    if (userToFollow && userToFollow.id !== user.value.id) {
      if (userToFollow.id === followingUser.value) {
        followingUser.value = null;
      } else {
        followingUser.value = userToFollow.id;
      }
      useTrackEvent({
        eventName: eventNames.user.followed,
        label: 'User followed',
        data: { user: followingUser.value },
        sendImmediately: true,
      });
    }
  };

  const me = async () => {
    try {
      const response = await api.get('/api/users/user/me/');

      if (![201, 200].includes(response.status)) {
        logoutUser();
        return;
      }
      user.value = response.data;
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      logoutUser();
    }
  };

  return {
    user,
    isAuthenticated,
    isLoading,
    knoxToken,
    checkAuthStatus,
    clearAuthState,
    loginWithProvider,
    logoutUser,
    getHeaders,
    hasAccessToken,
    getAccessToken,
    refreshToken,
    followUser,
    me,
  };
});
